import { Box, Flex, Text } from '@chakra-ui/react';
import { useState } from 'react';
import { FlagIcon } from '../icons';
import { GoalProgress, ProgressChartDataEntry, ThresholdField } from './types';

interface ProgressColumnCellProps {
  data?: GoalProgress;
  thresholdData?: ThresholdField;
}

const CustomTooltip = ({ active, payload }: any) => {
  if (active && payload && payload.length) {
    return (
      <Box
        bg="white"
        p={2}
        border="1px solid"
        borderColor={'text.tertiary'}
        borderRadius="4px"
        boxShadow="0px 2px 4px rgba(0, 0, 0, 0.1)"
        minW={'164px'}
      >
        <Text fontWeight="semibold" fontSize={'xs'}>
          {payload[0].payload.xlabelName}
        </Text>
        <Text fontSize={'xs'}>
          Goal Progress: {payload[0].value.toFixed(2)}%
        </Text>
      </Box>
    );
  }
  return null;
};

export const ProgressColumnCell = ({
  data,
  thresholdData,
}: ProgressColumnCellProps) => {
  const chartData: ProgressChartDataEntry[] =
    typeof data?.data === 'string' ? JSON.parse(data.data) : data?.data || [];

  const [activeTooltip, setActiveTooltip] = useState<{
    index: number;
    show: boolean;
  }>({ index: -1, show: false });

  const customTooltipProps = {
    active: activeTooltip.show,
    payload:
      activeTooltip.index >= 0
        ? [
            {
              payload: chartData[activeTooltip.index],
              value: chartData[activeTooltip.index]?.y || 0,
            },
          ]
        : [],
  };

  return (
    <Flex direction={'column'} gap={4} h={'100%'}>
      <Flex justify={'space-between'} align={'center'} h={'100%'}>
        <Flex align={'center'} gap={1}>
          <FlagIcon />
          <Text color={'text.gray.300'}>
            {thresholdData?.high
              ? `< ${thresholdData?.high}`
              : thresholdData?.low
              ? `> ${thresholdData?.low}`
              : ''}
            %
          </Text>
        </Flex>
        <Flex align={'center'} gap={2} w={'60%'} justify={'space-between'}>
          <Box w={'85%'} h={'8px'} rounded={'4px'} bg={'#E9E9E9'}>
            <Box
              bg={data?.color}
              w={`${data?.successRate}%`}
              h={'full'}
              rounded={'4px'}
            />
          </Box>
          <Text color={'text.gray.300'} fontWeight={'medium'}>
            {data?.successRate}%
          </Text>
        </Flex>
      </Flex>
      <Box h="80px" w="100%" pb={2} pr={1}>
        <Flex
          h="100%"
          alignItems="flex-end"
          justifyContent="flex-end"
          gap="12px"
          position="relative"
          w={'fit-content'}
          ml={'auto'}
        >
          <Box
            position="absolute"
            zIndex="100"
            left={
              activeTooltip.index >= 0
                ? `${activeTooltip.index * 20 - 50}px`
                : '0'
            }
            top="-20px"
            pointerEvents="none"
            opacity={activeTooltip.show ? 1 : 0}
            transition="opacity 0.2s"
          >
            <CustomTooltip {...customTooltipProps} />
          </Box>
          {chartData.map((entry: ProgressChartDataEntry, index: number) => (
            <Box
              key={`bar-${index}`}
              width="8px"
              height="50%"
              bg={entry.color}
              onMouseEnter={() => setActiveTooltip({ index, show: true })}
              onMouseLeave={() => setActiveTooltip({ index: -1, show: false })}
            />
          ))}
        </Flex>
      </Box>
    </Flex>
  );
};
